// ============================================================================
// Vendor Dashboard — the list behind a clicked Summary figure. Opens as a Drawer
// (state is a single nullable metric, the house convention).
//
// No fetch: the rows are the ones the page has ALREADY loaded and filtered, sliced
// by `rowsForMetric` — the same function the tile counted with — so the drawer's
// row count always equals the number that was clicked. Paginated client-side for
// the same reason: there is nothing to ask the server for.
// ============================================================================
import { Drawer, Pagination, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import CopyText from "../components/copy_text";
import { LogisticsOrderRow } from "../types";
import { dash, dispatchLabel } from "../logistics/board_columns";
import { DeliveryStatusTag } from "../logistics/components/logistics_status_tag";
import { requestTitle, SummaryRequest } from "./summary";
import { CarrierScope, listCarrierBookings } from "./carrier.api";
import BreachTag from "./breach_tag";
import EscalationRaisedTag from "./escalation_tag";

interface Props {
  request: SummaryRequest | null; // non-null opens the drawer
  scope: CarrierScope; // the page's filters — the drawer asks the server for its own slice
  onClose: () => void;
}

const columns: ColumnsType<LogisticsOrderRow> = [
  {
    // The shipment: its allotment PO number(s), stacked — a consolidated truck carries several.
    // Falls back to the booking id, which every shipment has even before a PO is stamped on it.
    title: "Shipment / PO no.",
    key: "po",
    width: 200,
    // Same shape as the Trips tab's PO cell: the number(s), then the escalation marker beneath —
    // a shipment flagged on its trip date must read the same wherever it is listed.
    render: (_, r) => (
      <div>
        {r.po_numbers?.length ? (
          r.po_numbers.map((po) => (
            <CopyText key={po} value={po} label="PO number">
              <div style={{ whiteSpace: "nowrap", color: "#1677ff" }}>{po}</div>
            </CopyText>
          ))
        ) : (
          <span style={{ color: "#8c8c8c" }}>#{r.id}</span>
        )}
        <EscalationRaisedTag row={r} />
      </div>
    ),
  },
  {
    title: "Sales order no.",
    key: "order",
    width: 160,
    render: (_, r) =>
      r.sales_order_id || r.zoho_salesorder_number ? (
        <CopyText
          value={r.zoho_salesorder_number || String(r.sales_order_id)}
          label="SO number"
        >
          <span style={{ color: "#1677ff", whiteSpace: "nowrap" }}>
            {r.zoho_salesorder_number || `SO #${r.sales_order_id}`}
          </span>
        </CopyText>
      ) : (
        dash(null)
      ),
  },
  {
    title: "Status",
    key: "status",
    width: 130,
    render: (_, r) => <DeliveryStatusTag status={r.delivery_status} />,
  },
  {
    // The driver stamped for the trip (the same name the Vendor is derived from), falling back to
    // the booking's own column when the trip payload carries none.
    title: "Driver name",
    key: "driver",
    width: 190,
    render: (_, r) => {
      const name = r.trip_driver_name || r.driver_name;
      if (!name) return <span style={{ color: "#bbb" }}>Not assigned</span>;
      return (
        <Tooltip title={r.trip_name ? `Trip ${r.trip_name}` : undefined}>
          <span>{name}</span>
        </Tooltip>
      );
    },
  },
  {
    // The same verdict (and the same hover reason) the Trips tab shows — this drawer is a list of
    // the same shipments, and a figure drilled into from the Breached cell would be strange without
    // the column that explains each row's verdict.
    title: "Breach",
    key: "breach",
    width: 110,
    render: (_, r) => <BreachTag row={r} />,
  },
  {
    // The dispatch day + window — the same axis the date filter above works on.
    title: "Date",
    key: "date",
    width: 110,
    render: (_, r) => dispatchLabel(r),
  },
];

const SummaryListDrawer: React.FC<Props> = ({ request, scope, onClose }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pageRows, setPageRows] = useState<LogisticsOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // A new drill-down starts at page 1 (kept out of render so paging within one doesn't reset).
  useEffect(() => {
    setPage(1);
  }, [request]);

  // The rows behind a tile come from the server, filtered by the very same `{metric, mile, sla}`
  // the tile counted — so the drawer's list and the figure that opened it are one query apart, not
  // two implementations apart.
  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    setLoading(true);
    listCarrierBookings({
      ...scope,
      metric: request.metric,
      mile: request.mile,
      sla: request.sla,
      page,
      page_size: pageSize,
    }).then((res) => {
      if (cancelled) return;
      setPageRows(res?.results ?? []);
      setTotal(res?.count ?? 0);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [request, scope, page, pageSize]);

  return (
    <Drawer
      title={
        request
          ? `${requestTitle(request)} — ${total} shipment${total === 1 ? "" : "s"}`
          : ""
      }
      width={900}
      open={!!request}
      onClose={onClose}
      destroyOnClose
    >
      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={pageRows}
        loading={loading}
        pagination={false}
        scroll={{ x: 900 }}
      />
      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          pageSizeOptions={["20", "50", "100", "200"]}
          showTotal={(t) => `${t} shipment${t === 1 ? "" : "s"}`}
          onChange={(p, size) => {
            setPage(p);
            setPageSize(size);
          }}
        />
      </div>
    </Drawer>
  );
};

export default SummaryListDrawer;
